import { useCallback, useEffect, useState } from "react";
import { getPublicDocuments } from "../lib/firebase";
import { sampleContent } from "../data/sampleContent";
import { extractYouTubeId } from "../utils/helpers";

const sortContent = (items) => [...items].sort((a, b) => {
  if (typeof a.order === "number" || typeof b.order === "number") {
    return (a.order ?? 999) - (b.order ?? 999);
  }
  return String(b.date || "").localeCompare(String(a.date || ""));
});

const getSharePointAudio = async () => {
  const endpoint = import.meta.env.VITE_SHAREPOINT_AUDIO_API || "/api/sharepoint-audio";
  const response = await fetch(endpoint, { headers: { Accept: "application/json" } });
  const contentType = response.headers.get("content-type") || "";
  if (!response.ok) throw new Error(`OneDrive audio request failed with status ${response.status}`);
  if (!contentType.includes("application/json")) throw new Error("OneDrive audio returned an invalid response");
  const payload = await response.json();
  return Array.isArray(payload.tracks) ? payload.tracks : [];
};

const getYouTubeVideos = async () => {
  const endpoint = import.meta.env.VITE_YOUTUBE_VIDEOS_API || "/api/youtube-videos";
  const response = await fetch(endpoint, { headers: { Accept: "application/json" } });
  const contentType = response.headers.get("content-type") || "";
  if (!response.ok) throw new Error(`YouTube video request failed with status ${response.status}`);
  if (!contentType.includes("application/json")) throw new Error("YouTube videos returned an invalid response");
  const payload = await response.json();
  return Array.isArray(payload.videos) ? payload.videos : [];
};

const mergeUniqueAudio = (...sources) => sources
  .flat()
  .filter((item) => item?.audioUrl)
  .filter((item, index, items) =>
    items.findIndex((candidate) =>
      candidate.id === item.id || candidate.audioUrl === item.audioUrl
    ) === index
  );

const mergeUniqueVideos = (...sources) => sources
  .flat()
  .map((item) => ({
    ...item,
    youtubeId: item.youtubeId || extractYouTubeId(item.youtubeUrl)
  }))
  .filter((item) => item.youtubeId || item.videoUrl)
  .filter((item, index, items) =>
    items.findIndex((candidate) =>
      (item.youtubeId && candidate.youtubeId === item.youtubeId)
      || (item.videoUrl && candidate.videoUrl === item.videoUrl)
    ) === index
  );

const legacyGalleryIds = new Set(["album-1", "album-2"]);

const mergeWithBundled = (bundledItems, remoteItems) => {
  const remoteById = new Map(remoteItems.map((item) => [item.id, item]));
  const merged = [];

  bundledItems.forEach((bundled) => {
    const replacement = remoteById.get(bundled.id);
    if (!replacement) {
      merged.push(bundled);
      return;
    }
    remoteById.delete(bundled.id);
    if (replacement.status === "published") merged.push({ ...bundled, ...replacement });
  });

  remoteById.forEach((item) => {
    if (item.status === "published") merged.push(item);
  });
  return merged;
};

export function useContent(collectionName) {
  const [items, setItems] = useState(() =>
    collectionName === "audio" || collectionName === "videos"
      ? []
      : sortContent(sampleContent[collectionName] || [])
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (collectionName === "audio") {
        const [oneDriveResult, firebaseResult] = await Promise.allSettled([
          getSharePointAudio(),
          getPublicDocuments("audio")
        ]);
        const oneDriveItems = oneDriveResult.status === "fulfilled" ? oneDriveResult.value : [];
        const firebaseItems = firebaseResult.status === "fulfilled" ? firebaseResult.value || [] : [];
        const combined = mergeUniqueAudio(oneDriveItems, firebaseItems);
        setItems(sortContent(combined));
        if (!combined.length && (oneDriveResult.status === "rejected" || firebaseResult.status === "rejected")) {
          setError("The audio library is temporarily unavailable.");
        }
      } else if (collectionName === "videos") {
        const [firebaseResult, youtubeResult] = await Promise.allSettled([
          getPublicDocuments("videos"),
          getYouTubeVideos()
        ]);
        const firebaseItems = firebaseResult.status === "fulfilled" ? firebaseResult.value || [] : [];
        const youtubeItems = youtubeResult.status === "fulfilled" ? youtubeResult.value : [];
        const combined = mergeUniqueVideos(firebaseItems, youtubeItems);
        setItems(sortContent(combined));
        if (!combined.length && (firebaseResult.status === "rejected" || youtubeResult.status === "rejected")) {
          setError("The video library is temporarily unavailable.");
        }
      } else if (collectionName === "galleryAlbums") {
        const localAlbums = sampleContent.galleryAlbums || [];
        const firestoreAlbums = (await getPublicDocuments(collectionName) || [])
          .filter((item) => !legacyGalleryIds.has(item.id));
        const combined = mergeWithBundled(localAlbums, firestoreAlbums);
        setItems(sortContent(combined));
      } else if (collectionName === "websiteSettings") {
        const firestoreItems = await getPublicDocuments(collectionName);
        setItems(sortContent(firestoreItems?.length ? firestoreItems : sampleContent.websiteSettings || []));
      } else {
        const firestoreItems = await getPublicDocuments(collectionName) || [];
        const combined = mergeWithBundled(sampleContent[collectionName] || [], firestoreItems);
        setItems(sortContent(combined));
      }
    } catch {
      if (collectionName === "audio" || collectionName === "videos") {
        setItems([]);
        setError(`The ${collectionName === "audio" ? "audio" : "video"} library is temporarily unavailable.`);
      } else {
        setItems(sortContent(sampleContent[collectionName] || []));
      }
    } finally {
      setLoading(false);
    }
  }, [collectionName]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { items, loading, error, refresh };
}
